/**
 * Operation Costs Service
 * Frontend service for calculating operation costs for manufacturers
 */

import axios from 'axios';
import { currencyFormattingService } from './currency-formatting.service';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

export interface CostCalculationResult {
  operationType: string;
  quantity: number;
  perUnitCost: number;
  totalCost: number;
  currency: string;
  calculatedAt: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  error?: string;
}

class OperationCostsService {
  private getAuthHeader() {
    const token = localStorage.getItem('auth_token');
    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  }

  /**
   * Calculate cost for a given operation and quantity
   */
  async calculateCost(operationType: string, quantity: number): Promise<CostCalculationResult> {
    try {
      const response = await axios.post<ApiResponse<CostCalculationResult>>(
        `${API_URL}/operation-costs/calculate`,
        {
          operationType: operationType.toLowerCase(),
          quantity,
        },
        this.getAuthHeader()
      );
      
      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to calculate cost');
      }
      
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        // If the endpoint doesn't exist, fall back to default calculation
        return this.calculateDefaultCost(operationType, quantity);
      }
      throw error;
    }
  }

  /**
   * Get current rate for an operation type
   */
  async getCurrentRate(operationType: string): Promise<number> {
    try {
      const result = await this.calculateCost(operationType, 1);
      return result.perUnitCost;
    } catch (error) {
      // Fallback to default rates
      const defaultRates: Record<string, number> = {
        'batch_creation': 0.1,
        'transfer': 0.05,
        'verification': 0.02,
      };
      return defaultRates[operationType.toLowerCase()] || 0.1;
    }
  }

  /**
   * Format currency value for display
   */
  formatCurrency(amount: number, currency: string = 'INR'): string {
    return currencyFormattingService.formatCurrency(amount, currency);
  }

  /**
   * Fallback calculation when API is not available
   */
  private calculateDefaultCost(operationType: string, quantity: number): CostCalculationResult {
    const defaultRates: Record<string, number> = {
      'batch_creation': 0.1,
      'transfer': 0.05,
      'verification': 0.02,
    };

    const perUnitCost = defaultRates[operationType.toLowerCase()] || 0.1;
    const totalCost = perUnitCost * quantity;

    return {
      operationType,
      quantity,
      perUnitCost,
      totalCost,
      currency: 'INR',
      calculatedAt: new Date().toISOString(),
    };
  }
}

export const operationCostsService = new OperationCostsService();